import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@et/environment';
import {
  AdminUsersQueryParams,
  AdminUserV2,
  EJournalBody,
  ETJournalAPIResponse,
  Profile,
  RequiredMfaMethod,
  SelfProfile,
  SelfProfileApiResponse,
} from '@et/typings';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProfileV2ApiService {
  private http = inject(HttpClient);

  /**
   * Current user profile from profile service `/self` (authorization context).
   */
  getSelfProfile(): Observable<SelfProfile> {
    return this.http
      .get<SelfProfileApiResponse>(`${environment.profilev2.domain}/self`)
      .pipe(map((res) => res.data));
  }

  /**
   * Updates a user's profile
   * @param profileId - profile id
   * @param profile - profile
   * @returns Observable of the updated profile
   */
  updateProfile(
    profileId: string,
    profile: Partial<Profile> & {
      preferredFirstFactor?: string;
      preferredMfaChannel?: string;
      phone?: string | null;
    },
  ): Observable<Profile> {
    return this.http
      .patch<{ data: Profile }>(
        `${environment.profilev2.domain}/api/profiles/${profileId}`,
        profile,
      )
      .pipe(map((res) => res.data));
  }

  /**
   * Anonymous environment probe: `{ auth: "AAD" | "Better Auth" }`.
   * Used by iOS/Android to pick a login provider before a token exists.
   */
  getPublicAuth(): Observable<{ auth: string }> {
    return this.http.get<{ auth: string }>(
      `${environment.profilev2.domain}/public/auth`,
    );
  }

  /**
   * Anonymous email-first probe: preferred first factor, company MFA flag, and
   * the effective required 2FA method (profile override, else company).
   * Unknown emails resolve to Password / mfaEnabled false / requiredMfaMethod None
   * (anti-enumeration).
   */
  getSignInMethod(email: string): Observable<{
    firstFactor: string;
    mfaEnabled: boolean;
    requiredMfaMethod: RequiredMfaMethod;
  }> {
    return this.http
      .post<{
        data: {
          firstFactor: string;
          mfaEnabled: boolean;
          requiredMfaMethod?: string;
        };
      }>(`${environment.profilev2.domain}/api/profiles/sign-in-method`, {
        email,
      })
      .pipe(
        map((res) => ({
          firstFactor: res.data?.firstFactor ?? 'Password',
          mfaEnabled: res.data?.mfaEnabled === true,
          requiredMfaMethod: parseRequiredMfaMethod(
            res.data?.requiredMfaMethod,
          ),
        })),
      );
  }

  /**
   * This function takes a user profile id and returns ET Journals of passed user
   * @param {String} profileId - user id
   */
  getEjournalsByProfileId(profileId: string) {
    return this.http
      .get<ETJournalAPIResponse>(
        environment.profilev2.domain + '/api/ejournals/profile/' + profileId,
      )
      .pipe(map((res) => res.data));
  }

  /**
   * This function takes a journal id and returns ET Journal entity
   * @param {String} journalId - journal id
   */
  getEJournalById(journalId: string) {
    return this.http
      .get<ETJournalAPIResponse>(
        environment.profilev2.domain + '/api/ejournals/' + journalId,
      )
      .pipe(map((res) => res.data));
  }

  /**
   * This function takes a EJournalEntry and creates eJournal
   * @param {EJournalBody} eJournal - EJournalEntry
   */
  createEJournal(eJournal: EJournalBody) {
    return this.http.post<ETJournalAPIResponse>(
      environment.profilev2.domain + '/api/ejournals',
      eJournal,
    );
  }

  /**
   * This function takes a query params and returns a list of users
   * @param params - query params
   * @returns Observable of the list of users
   */
  getUsers(params: AdminUsersQueryParams) {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== ''),
    );
    return this.http
      .get<{ data: AdminUserV2[] }>(
        `${environment.profilev2.domain}/api/Profiles`,
        { params: query },
      )
      .pipe(map(({ data }) => data));
  }
}

function parseRequiredMfaMethod(raw: string | undefined): RequiredMfaMethod {
  switch ((raw ?? '').toLowerCase()) {
    case 'email':
      return 'Email';
    case 'sms':
      return 'Sms';
    case 'authenticator':
      return 'Authenticator';
    case 'none':
      return 'None';
    default:
      return 'None';
  }
}
